import querystring from 'querystring';
import type { GetServerSideProps, NextPage } from 'next';
import { z } from 'zod';
import Script from 'next/script';
import { useEffect } from 'react';
import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import { modularPageWrapperSchema } from '../../components/ModularPageWrapper/schema';
import { type CachedEntryResponse, getCachedEntry } from '../../utils/requests/contentstack/cachedRequests';
import { formatError, serverLogger } from '../../utils/loggers/server';
import { ModularPageWrapper } from '../../components/ModularPageWrapper';
import { getReactivationUrl, isDynamicAdsLandingPage } from '../../utils/redirects';
import { getContentVariant, getContentstackEntryUid } from '../../utils/getContentVariant/getContentVariant';
import { isAllSettledResponseFailure, logResponsesFromAllSettledSSR } from '../../utils/promises';
import { getDynamicAdsRequest } from '../../features/dynamicAds/utils/getDynamicAdsRequest';
import { getModulesWithHeroOverride } from '../../features/dynamicAds/utils/getModulesWithHeroOverride';
import { setOfferOverride } from '../../utils/modules/setOfferOverride';
import { getDynamicAdsContentstackUid } from '../../features/dynamicAds/paths';
import { dynamicAdHeroSchema } from '../../features/dynamicAds/components/DynamicAdsHero/schema';
import { addFeatureFlagsToRumContext } from '../../utils/loggers/browser';
import { type LandingPageHeroCopyAllocationValue, getLandingPageHeroCopyAllocation, overrideHeroModuleByAllocation } from '../../experiments/landingPageHeroCopy';
import { LANDING_PAGE_HERO_COPY_CONFIG_PARAM_NAME } from '../../utils/helpers';
import { jsonLdData } from './jsonLdData';
export type WomenPageProps = z.infer<typeof modularPageWrapperSchema> & {
  landingPageHeroCopyAllocationValue: LandingPageHeroCopyAllocationValue;
};
const WOMEN_ENTRY_UIDS = {
  supTarget: 'blt45c12091a3625be3',
  control: 'blt804f82dd5e51b628',
  q3Redesign: 'bltaea0fc39ebffc4b0'
};
export const getServerSideProps: GetServerSideProps<WomenPageProps> = async ({
  req,
  query
}) => {
  const url = req.url || '';
  const pathname = new URL(url, 'https://www.stitchfix.com').pathname;
  const stringifiedQuery = querystring.stringify(query);
  const reactivationUrl = getReactivationUrl(url);
  if (reactivationUrl) {
    return {
      redirect: {
        destination: `${reactivationUrl}?${stringifiedQuery}`,
        permanent: false
      }
    };
  }
  const contentVariant = getContentVariant({
    url
  });
  try {
    const landingPageHeroCopyAllocation = await getLandingPageHeroCopyAllocation({
      page: 'women',
      req,
      url
    });
    const entryRequest = await getCachedEntry({
      contentType: 'modular_page',
      uid: getContentstackEntryUid({
        contentVariant,
        uids: WOMEN_ENTRY_UIDS
      }),
      schema: modularPageWrapperSchema,
      logger: serverLogger
    });
    let dynamicAdsRequest: ReturnType<typeof getDynamicAdsRequest> | null = null;
    let dynamicAdsContentRequest: Promise<CachedEntryResponse<z.infer<typeof dynamicAdHeroSchema>>> | null = null;

    // only call the dynamic ads algos if the page is a dynamic ads landing page
    if (isDynamicAdsLandingPage(url)) {
      dynamicAdsContentRequest = getCachedEntry({
        contentType: 'dynamic_ad_hero',
        uid: getDynamicAdsContentstackUid(pathname),
        schema: dynamicAdHeroSchema,
        logger: serverLogger
      });
      dynamicAdsRequest = getDynamicAdsRequest({
        req
      });
    }

    /**
     * this allows us to handle a dynamic number of requests in a non-synchronous way
     */
    const responses = await Promise.allSettled([entryRequest, dynamicAdsRequest, dynamicAdsContentRequest]);
    logResponsesFromAllSettledSSR({
      responses,
      message: 'women data error',
      team: 'client-onboarding'
    });
    const [entryResponse, dynamicAdsResponse, dynamicAdsContentResponse] = responses;
    if (isAllSettledResponseFailure(entryResponse)) {
      throw entryResponse.reason;
    }
    const modules = entryResponse.value.data.modules;
    const dynamicAdsData = dynamicAdsResponse.status === 'fulfilled' ? dynamicAdsResponse.value?.data : undefined;
    const dynamicAdsContentData = dynamicAdsContentResponse.status === 'fulfilled' ? dynamicAdsContentResponse.value?.data : undefined;
    const heroModules = getModulesWithHeroOverride({
      businessLinePage: 'women',
      modules,
      dynamicAds: dynamicAdsData,
      dynamicAdHeroContent: dynamicAdsContentData
    });
    const modulesWithOfferOverride = setOfferOverride(heroModules);
    const modulesWithHeroExperimentOverride = await overrideHeroModuleByAllocation({
      modules: modulesWithOfferOverride,
      allocationValue: landingPageHeroCopyAllocation.allocationValue,
      page: 'women'
    });
    return {
      props: {
        seoData: entryResponse.value.data.seoData,
        modules: modulesWithHeroExperimentOverride,
        landingPageHeroCopyAllocationValue: landingPageHeroCopyAllocation.allocationValue
      }
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Required page data is undefined - Women',
      context: {
        error: formatError(error),
        page: url,
        severity: 'critical'
      }
    });
    throw new Error('Required data not provided to render Women page');
  }
};
const WomenPage: NextPage<WomenPageProps> = ({
  seoData,
  modules,
  landingPageHeroCopyAllocationValue
}) => {
  useEffect(() => {
    fireEvent({
      eventName: 'expt_1773',
      eventAttributes: {
        allocationValue: landingPageHeroCopyAllocationValue
      }
    });
    addFeatureFlagsToRumContext({
      [LANDING_PAGE_HERO_COPY_CONFIG_PARAM_NAME]: landingPageHeroCopyAllocationValue
    });
  }, [landingPageHeroCopyAllocationValue]);
  return <>
      <Script
    // Blueshift web conversion integration (https://experiences.getblueshift.com/)
    data-testid="blueshift-script" src="https://www.lightboxcdn.com/vendor/3767d51a-4af9-4270-8ee4-c7310e4371b0/lightbox_speed.js" defer />
      <ModularPageWrapper seoData={seoData} modules={modules} jsonLdData={jsonLdData} />
    </>;
};
export default WomenPage;